import React, { useState, useCallback, useMemo } from "react";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import {
  PositionData,
  ClosedPositionData,
} from "@crypto-trading/common/browser";
import { Loader2, Plus } from "lucide-react";
import { DesktopLayout } from "@/layouts/DesktopLayout";
import {
  ExchangeFilterSelect,
  ExchangeFilterOption,
} from "@/components/ExchangeFilterSelect";
import { ExchangesStripCard } from "@/components/ExchangesStripCard";
import { TradeStatsCards } from "@/components/TradeStatsCards";
import { EmptyState } from "@/components/ui/EmptyState";
import { useOpenPositions } from "@/hooks/usePositions";
import { trackEvent } from "@/utils/analytics";

// Desktop drawer components
import {
  PositionDetailDrawerDesktop,
  PositionTradeDrawerDesktop,
  TickerSymbolDrawerDesktop,
} from "./drawers";
import { PositionTile } from "@/components/PositionTile";
import { AssetClassToggle, type AssetClass } from "@/components/AssetClassToggle";
import { useStockInstruments } from "@/hooks/useStockNews";
import { buildStockTradeTarget, isStockPosition, type StockTradeTarget } from "@/stock/stock-trade";
import { StockTickerDrawerDesktop } from "@/pages/stock-alerts/drawers/StockTickerDrawer.Desktop";

export const AllPositionsPageDesktop: React.FC = () => {
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || "";

  const [selectedPosition, setSelectedPosition] = useState<
    PositionData | ClosedPositionData | null
  >(null);
  const [isTickerDrawerOpen, setIsTickerDrawerOpen] = useState(false);
  const [isTradeDrawerOpen, setIsTradeDrawerOpen] = useState(false);
  const [tradeSymbol, setTradeSymbol] = useState<string | null>(null);
  const [exchangeFilter, setExchangeFilter] =
    useState<ExchangeFilterOption>("all");
  const [assetClass, setAssetClass] = useState<AssetClass>("crypto");
  const [stockTickerOpen, setStockTickerOpen] = useState(false);
  const [stockTradeTarget, setStockTradeTarget] =
    useState<StockTradeTarget | null>(null);

  // React Query hooks - only for open positions
  const exchangeCodeFilter =
    exchangeFilter === "all" ? undefined : exchangeFilter;
  const { data: openPositions = [], isLoading } = useOpenPositions(
    userId,
    true,
    exchangeCodeFilter
  );
  const { data: stockInstruments = [], isSuccess: stockCatalogReady } =
    useStockInstruments();
  const stockTickers = useMemo(
    () => new Set(stockInstruments.map((row) => row.ticker)),
    [stockInstruments]
  );
  const stockTickerList = useMemo(
    () => [...stockTickers].sort(),
    [stockTickers]
  );
  const visiblePositions = openPositions.filter((position) => {
    if (!stockCatalogReady) return assetClass === "crypto";
    const stock = isStockPosition(position.symbol, stockTickers);
    return assetClass === "stocks" ? stock : !stock;
  });

  const handleOpenTickerDrawer = () => {
    trackEvent("custom_trade_attempt", { page: "positions", userId });
    if (assetClass === "stocks") {
      setStockTickerOpen(true);
      return;
    }
    setIsTickerDrawerOpen(true);
  };

  const handleTickerSubmit = useCallback((normalizedSymbol: string) => {
    setTradeSymbol(normalizedSymbol);
    setIsTickerDrawerOpen(false);
    setIsTradeDrawerOpen(true);
  }, []);

  const handleTradeDrawerClose = (open: boolean) => {
    setIsTradeDrawerOpen(open);
    if (!open) {
      setTradeSymbol(null);
      setStockTradeTarget(null);
    }
  };

  const handleStockTickerSelect = useCallback(
    (ticker: string) => {
      const target = buildStockTradeTarget(ticker, stockInstruments);
      setStockTradeTarget(target);
      setTradeSymbol(target.symbol);
      setStockTickerOpen(false);
      setIsTradeDrawerOpen(true);
    },
    [stockInstruments]
  );

  const newTradeButton = (
    <button
      type="button"
      onClick={handleOpenTickerDrawer}
      className="flex h-11 items-center justify-center gap-1.5 rounded-xl border-2 border-white/[0.12] bg-[#07a66c] px-4 font-semibold text-white shadow-[inset_0_0_0_1px_rgba(0,0,0,0.18),inset_0_-2px_0_rgba(0,0,0,0.05)] transition-colors hover:bg-[#07a66c]/85"
    >
      <Plus className="size-5" />
      <span>New Trade</span>
    </button>
  );

  return (
    <>
      <DesktopLayout
        pageTitle="Trade"
        pageDescription="Manage and monitor your open positions across all your connected exchanges."
        showRightSidebar={false}
        headerActions={<ExchangesStripCard userId={userId} className="w-[400px]" />}
      >
        <div className="flex flex-col gap-8">
          <div className="flex items-center gap-3">
            <div className="w-[220px]">
              <AssetClassToggle value={assetClass} onChange={setAssetClass} />
            </div>
            <div className="flex flex-1 items-center justify-end gap-3">
              {!isLoading && visiblePositions.length > 0 && newTradeButton}
              <span className="text-sm font-medium text-[#a3a3a3]">Exchange</span>
              <ExchangeFilterSelect
                value={exchangeFilter}
                onChange={setExchangeFilter}
                className="w-[220px]"
              />
            </div>
          </div>

          <TradeStatsCards userId={userId} positions={visiblePositions} />

          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-accent" />
            </div>
          )}

          {!isLoading && visiblePositions.length === 0 && (
            <EmptyState
              title="No Open Positions Yet"
              description="Connect an exchange and start trading to see your open positions here."
              action={<div className="w-[248px] [&>button]:w-full">{newTradeButton}</div>}
            />
          )}

          {!isLoading && visiblePositions.length > 0 && (
            <div
              className="grid gap-4"
              style={{
                gridTemplateColumns: "repeat(auto-fill, minmax(500px, 1fr))",
              }}
            >
              {visiblePositions.map((position) => (
                <PositionTile
                  key={position.positionId}
                  position={position}
                  onClick={() => setSelectedPosition(position)}
                />
              ))}
            </div>
          )}
        </div>
      </DesktopLayout>

      {/* Desktop Sheets */}
      <TickerSymbolDrawerDesktop
        open={isTickerDrawerOpen}
        onOpenChange={setIsTickerDrawerOpen}
        onSubmit={handleTickerSubmit}
        title="Enter Ticker Symbol"
        submitButtonText="Continue to Trade"
      />

      <StockTickerDrawerDesktop
        open={stockTickerOpen}
        onOpenChange={setStockTickerOpen}
        tickers={stockTickerList}
        onSelect={handleStockTickerSelect}
      />

      <PositionTradeDrawerDesktop
        userId={userId}
        symbol={tradeSymbol}
        displayTicker={stockTradeTarget?.displayTicker}
        allowedExchangeCodes={stockTradeTarget?.allowedExchangeCodes}
        symbolByExchangeCode={stockTradeTarget?.symbolByExchangeCode}
        open={isTradeDrawerOpen}
        onOpenChange={handleTradeDrawerClose}
        onGoToPosition={(position) => {
          if (position) {
            setSelectedPosition(position);
            setIsTradeDrawerOpen(false);
          }
        }}
      />

      <PositionDetailDrawerDesktop
        userId={userId}
        position={selectedPosition}
        open={!!selectedPosition}
        onOpenChange={(open) => !open && setSelectedPosition(null)}
        onPositionUpdate={(updatedPosition) =>
          setSelectedPosition(updatedPosition)
        }
        source="positions_page"
      />
    </>
  );
};
